// Cloud-only view of the user's own samples. Lists every row owned
// by the signed-in user (across all their patches, including the
// auto-created patch the MCP uploads land on).
//
// Two actions per row:
//   - row click  → audition (one-shot preview, no IDB write, no
//                  statement insert). Re-click stops; click another
//                  row swaps the voice.
//   - download   → pull-to-local: full import + register with Strudel
//                  + insert `const <name> = s("<name>")` into the song.
//                  After this the sample lives in the Local tab and
//                  plays in patterns.
//
// Shape mirrors CommunityLibrary so the two cloud-source surfaces
// feel identical; the differences are (a) the data source (Supabase
// RLS-gated rather than the public Worker endpoint), (b) the import
// filename origin label ("mine:" rather than "community:"), and (c)
// the sign-in gate — unauthenticated users have no cloud rows to list.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEditContext } from '../editContext';
import {
  auditionCloudSample,
  importMyCloudSample,
  isCloudConfigured,
  listMyCloudSamples,
  type MyCloudSample,
} from '../../cloud';
import { useSession } from '../../auth/useSession';
import { getAudioContext } from '../../strudel';
import { useSampleStore } from './SampleStoreContext';
import { makeLocalSampleStatement } from './sampleStatement';

export type MyCloudSamplesProps = {
  onStatus: (msg: string, level?: 'ok' | 'warn' | 'err' | '') => void;
};

type LoadState = 'idle' | 'loading' | 'error';

export function MyCloudSamples({ onStatus }: MyCloudSamplesProps) {
  const ops = useEditContext();
  const { samples: localSamples, storage, refresh: refreshSampleStore } = useSampleStore();
  const { session, loading: sessionLoading } = useSession();

  const [query, setQuery] = useState('');
  const [items, setItems] = useState<MyCloudSample[]>([]);
  const [loadState, setLoadState] = useState<LoadState>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [importingId, setImportingId] = useState<string | null>(null);
  const [auditioningId, setAuditioningId] = useState<string | null>(null);
  const [flashId, setFlashId] = useState<string | null>(null);
  const flashTimer = useRef<number | null>(null);
  // Stop fn for the in-flight audition voice. Lets re-clicking a
  // playing row cut the voice, and switching rows hand off cleanly.
  const auditionStopRef = useRef<(() => void) | null>(null);

  const localNames = useMemo(
    () => new Set(localSamples.map((s) => s.name)),
    [localSamples],
  );

  const fetchSamples = useCallback(async () => {
    setLoadState('loading');
    setErrorMsg(null);
    try {
      const rows = await listMyCloudSamples();
      setItems(rows);
      setLoadState('idle');
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : String(e));
      setItems([]);
      setLoadState('error');
    }
  }, []);

  useEffect(() => {
    if (sessionLoading) return;
    if (!session) {
      setItems([]);
      setLoadState('idle');
      return;
    }
    void fetchSamples();
  }, [session, sessionLoading, fetchSamples]);

  useEffect(() => {
    return () => {
      if (flashTimer.current) window.clearTimeout(flashTimer.current);
      auditionStopRef.current?.();
    };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((s) => {
      if (s.name.toLowerCase().includes(q)) return true;
      if (s.tags.some((t) => t.toLowerCase().includes(q))) return true;
      return false;
    });
  }, [items, query]);

  const audition = useCallback(
    async (cloud: MyCloudSample) => {
      // Same-row re-click cuts the voice.
      if (auditioningId === cloud.id) {
        auditionStopRef.current?.();
        auditionStopRef.current = null;
        setAuditioningId(null);
        return;
      }
      // Different row: stop whatever's playing first.
      auditionStopRef.current?.();
      auditionStopRef.current = null;

      const ctx = getAudioContext();
      if (!ctx) {
        onStatus('Audio engine not ready yet — try Play first.', 'warn');
        return;
      }
      setAuditioningId(cloud.id);
      try {
        const { stop, buffer } = await auditionCloudSample(cloud.r2Key, ctx);
        auditionStopRef.current = stop;
        // Clear the indicator when the voice ends naturally — the
        // AudioBuffer's duration is the ceiling.
        const ms = Math.max(50, Math.ceil(buffer.duration * 1000) + 50);
        window.setTimeout(() => {
          if (auditionStopRef.current === stop) {
            auditionStopRef.current = null;
            setAuditioningId((curr) => (curr === cloud.id ? null : curr));
          }
        }, ms);
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        onStatus('Audition failed: ' + msg, 'err');
        setAuditioningId(null);
      }
    },
    [auditioningId, onStatus],
  );

  const pull = useCallback(
    async (cloud: MyCloudSample) => {
      if (!storage) {
        onStatus('Sample storage not ready yet — try again in a moment.', 'warn');
        return;
      }
      const ctx = getAudioContext();
      if (!ctx) {
        onStatus('Audio engine not ready yet — try Play first.', 'warn');
        return;
      }
      setImportingId(cloud.id);
      try {
        const { sample } = await importMyCloudSample(cloud, ctx, storage);
        refreshSampleStore();

        let insertedId: string | null = null;
        ops.update((draft) => {
          const taken = new Set<string>();
          for (const s of draft.statements) {
            if (s.kind === 'const') taken.add(s.name);
          }
          const stmt = makeLocalSampleStatement({
            sampleName: sample.name,
            takenNames: taken,
          });
          draft.statements.push(stmt);
          insertedId = stmt.id;
        });
        if (insertedId) ops.revealBlock(insertedId);

        setFlashId(cloud.id);
        if (flashTimer.current) window.clearTimeout(flashTimer.current);
        flashTimer.current = window.setTimeout(() => setFlashId(null), 550);
        onStatus(`Pulled "${sample.name}" — try s("${sample.name}").`, 'ok');
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        onStatus('Pull failed: ' + msg, 'err');
      } finally {
        setImportingId(null);
      }
    },
    [storage, ops, refreshSampleStore, onStatus],
  );

  if (!isCloudConfigured()) {
    return <CloudUnconfigured />;
  }

  if (sessionLoading) {
    return <Centered>Checking sign-in…</Centered>;
  }

  if (!session) {
    return <SignedOutHint />;
  }

  return (
    <div className="relative w-full h-full bg-zinc-950 flex flex-col min-h-0">
      <Search
        value={query}
        onChange={setQuery}
        visible={filtered.length}
        total={items.length}
        onRefresh={() => void fetchSamples()}
        refreshing={loadState === 'loading'}
      />

      <div
        aria-label="My cloud samples"
        className="flex-1 min-h-0 overflow-y-auto px-3 pb-6 pt-1 space-y-1.5 scrollbar-thin"
      >
        {loadState === 'loading' && items.length === 0 ? (
          <Centered>Loading…</Centered>
        ) : loadState === 'error' ? (
          <ErrorState message={errorMsg ?? 'Unknown error'} />
        ) : items.length === 0 ? (
          <EmptyHint />
        ) : filtered.length === 0 ? (
          <Centered>No matches for "{query}"</Centered>
        ) : (
          filtered.map((entry) => (
            <CloudRow
              key={entry.id}
              entry={entry}
              flashing={flashId === entry.id}
              auditioning={auditioningId === entry.id}
              importing={importingId === entry.id}
              alreadyLocal={localNames.has(entry.name)}
              onAudition={() => void audition(entry)}
              onPull={() => void pull(entry)}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ─── Pieces ─────────────────────────────────────────────────────────

function Search({
  value,
  onChange,
  visible,
  total,
  onRefresh,
  refreshing,
}: {
  value: string;
  onChange: (v: string) => void;
  visible: number;
  total: number;
  onRefresh: () => void;
  refreshing: boolean;
}) {
  return (
    <div className="px-4 pt-3 pb-2 flex items-center gap-2">
      <label className="relative block flex-1">
        <span className="sr-only">Search my cloud samples</span>
        <svg
          viewBox="0 0 16 16"
          className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-600"
          stroke="currentColor"
          strokeWidth={1.5}
          fill="none"
        >
          <circle cx={7} cy={7} r={4.5} />
          <path d="M10.5 10.5 L14 14" strokeLinecap="round" />
        </svg>
        <input
          type="text"
          inputMode="search"
          placeholder="search names, tags…"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-zinc-900/70 border border-zinc-800 focus:border-emerald-300/50 focus:bg-zinc-900 focus:outline-none rounded text-[11px] text-zinc-200 placeholder:text-zinc-600 placeholder:tracking-wide pl-8 pr-12 py-1.5 transition-colors font-body"
        />
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] text-zinc-600 tabular-nums tracking-wider">
          {visible}
          <span className="text-zinc-700">/{total}</span>
        </span>
      </label>
      <button
        type="button"
        onClick={onRefresh}
        disabled={refreshing}
        title="Refresh from cloud"
        aria-label="Refresh from cloud"
        className="shrink-0 grid place-items-center w-7 h-7 rounded-sm border border-zinc-800 text-zinc-500 hover:text-emerald-300 hover:border-emerald-300/50 disabled:opacity-30 transition-colors"
      >
        <RefreshIcon spinning={refreshing} />
      </button>
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-center text-[10px] uppercase tracking-[0.3em] text-zinc-600 py-8">
      {children}
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="text-center text-[10px] tracking-[0.2em] text-rose-300 py-6 px-3">
      <div className="uppercase mb-1">Couldn't load cloud samples</div>
      <div className="text-zinc-500 normal-case tracking-normal text-[11px]">
        {message}
      </div>
    </div>
  );
}

function CloudUnconfigured() {
  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-3 px-6 py-10 text-center">
      <div className="font-display text-[10px] tracking-[0.3em] uppercase text-zinc-500">
        Cloud not configured
      </div>
      <p className="font-body text-[11px] leading-[1.6] text-zinc-500 max-w-[220px]">
        Set <code className="text-zinc-300">VITE_SUPABASE_URL</code>,{' '}
        <code className="text-zinc-300">VITE_WORKER_URL</code> and friends in{' '}
        <code className="text-zinc-300">.env.local</code> to see your cloud library.
      </p>
    </div>
  );
}

function SignedOutHint() {
  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-3 px-6 py-10 text-center">
      <div className="font-display text-[10px] tracking-[0.3em] uppercase text-zinc-500">
        Sign in to see your cloud
      </div>
      <p className="font-body text-[11px] leading-[1.6] text-zinc-500 max-w-[220px]">
        Samples uploaded via the MCP server live in your account. Sign in (gear icon, top of Songs) to list and pull them onto this device.
      </p>
    </div>
  );
}

function EmptyHint() {
  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-3 px-6 py-10 text-center">
      <div className="font-display text-[10px] tracking-[0.3em] uppercase text-zinc-500">
        No cloud samples yet
      </div>
      <p className="font-body text-[11px] leading-[1.6] text-zinc-500 max-w-[220px]">
        Upload via the MCP server (<code className="text-zinc-300">publish_sample_from_path</code>) and they'll appear here on next refresh.
      </p>
    </div>
  );
}

// ─── Row ────────────────────────────────────────────────────────────

function CloudRow({
  entry,
  flashing,
  auditioning,
  importing,
  alreadyLocal,
  onAudition,
  onPull,
}: {
  entry: MyCloudSample;
  flashing: boolean;
  auditioning: boolean;
  importing: boolean;
  alreadyLocal: boolean;
  onAudition: () => void;
  onPull: () => void;
}) {
  return (
    <div
      className={
        'group relative flex items-stretch border bg-zinc-900/50 rounded-sm overflow-hidden transition-all ' +
        (flashing
          ? 'border-emerald-300 shadow-[0_0_0_1px_rgba(110,231,183,0.6)]'
          : auditioning
            ? 'border-emerald-300/60'
            : 'border-zinc-800 hover:border-emerald-300/30')
      }
    >
      <button
        type="button"
        onClick={onAudition}
        title={auditioning ? 'Stop audition' : `Audition "${entry.name}"`}
        className="flex-1 min-w-0 flex items-center gap-2.5 px-2.5 py-2 text-left"
      >
        <div
          aria-hidden="true"
          className="shrink-0 inline-grid place-items-center w-7 h-7 rounded-sm font-display text-[8px] tracking-[0.15em] uppercase font-semibold bg-emerald-300/15 text-emerald-200"
        >
          {auditioning ? <SpinnerDot /> : alreadyLocal ? 'DUP' : 'CLD'}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] text-zinc-100 font-body truncate">
            {entry.name}
          </div>
          <div className="text-[9px] text-zinc-500 tracking-wider tabular-nums flex items-center gap-2">
            <span>{(entry.durationMs / 1000).toFixed(2)}s</span>
            <span>·</span>
            <span>{formatBytes(entry.sizeBytes)}</span>
            {entry.tags.length > 0 && (
              <span className="truncate">· {entry.tags.slice(0, 3).join(', ')}</span>
            )}
          </div>
        </div>
      </button>

      <button
        type="button"
        onClick={onPull}
        disabled={importing}
        title={
          alreadyLocal
            ? `"${entry.name}" already exists locally — pulling will give it a unique suffix.`
            : importing
              ? 'Pulling to this device…'
              : `Pull s("${entry.name}") to this device`
        }
        aria-label={`Pull ${entry.name} to local`}
        className="shrink-0 w-8 grid place-items-center text-zinc-500 hover:text-emerald-300 disabled:text-zinc-700 disabled:cursor-not-allowed border-l border-zinc-800 transition-colors"
      >
        {importing ? <SpinnerDot /> : <PullIcon />}
      </button>
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function SpinnerDot() {
  return (
    <span
      className="inline-block w-2 h-2 rounded-full bg-emerald-300 animate-pulse"
      aria-hidden="true"
    />
  );
}

function PullIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      width={11}
      height={11}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M8 2 V11" />
      <path d="M4.5 7.5 L8 11 L11.5 7.5" />
      <path d="M3 13.5 H13" />
    </svg>
  );
}

function RefreshIcon({ spinning }: { spinning: boolean }) {
  return (
    <svg
      viewBox="0 0 16 16"
      width={12}
      height={12}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={spinning ? 'animate-spin' : ''}
    >
      <path d="M13 4 V8 H9" />
      <path d="M13 8 A5 5 0 1 0 11.5 11.5" />
    </svg>
  );
}
